import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { FormattedMessage } from 'react-intl';

import colors from '@app/constants/colors';

const WizardHeader = ({ steps, activeStepIndex, completedSteps }) => {
  const distanceBetweenSteps = 100 / (steps.length - 1);

  return (
    <ProgressContainer>
      <ProgressInnerContainer>
        <ProgressLine />
        {
          steps.map(({ labelId }, index) => (
            <WizardStep distanceBetweenSteps={distanceBetweenSteps * index} key={labelId}>
              <WizardStepInner inactive={index > activeStepIndex} isCompleted={completedSteps.includes(index)}>
                { completedSteps.includes(index) && <span>&#10003;</span> }
              </WizardStepInner>
              <StepName inactive={index > activeStepIndex}>
                <FormattedMessage id={labelId} />
              </StepName>
            </WizardStep>
          ))
        }
      </ProgressInnerContainer>
    </ProgressContainer>
  );
};

WizardHeader.propTypes = {
  steps: PropTypes.arrayOf(PropTypes.shape({
    labelId: PropTypes.string.isRequired,
    route: PropTypes.string,
  })).isRequired,
  activeStepIndex: PropTypes.number.isRequired,
  completedSteps: PropTypes.arrayOf(PropTypes.number),
};

WizardHeader.defaultProps = {
  completedSteps: [],
};

export default WizardHeader;

const ProgressContainer = styled.div`
  padding: 20px 50px 50px;
  width: 100%;
  text-align: center;
  
   @media (max-width: 650px) {
    padding-bottom: 70px;
   }
`;

const ProgressInnerContainer = styled.div`
  width: 500px;
  position: relative;
  margin: auto;
  content: '&#10003';
  
  @media (max-width: 650px) {
    width: 100%;
  }
`;

const ProgressLine = styled.hr`
  border: none;
  border-top: 1px solid white;
  margin: 0;
`;

const STEP_SIZE = 33;
const STEP_SMALL_SIZE = 25;
const STEP_BORDER = 3;
const STEP_SMALL_BORDER = 2;
const STEP_INNER_SIZE = STEP_SIZE - 4 * STEP_BORDER;
const STEP_SMALL_INNER_SIZE = STEP_SMALL_SIZE - 4 * STEP_SMALL_BORDER;

const wizardStepMixin = (stepWidth, distanceBetweenSteps, stepBorder) => `
  width: ${stepWidth}px;
  height: ${stepWidth}px;
  top: -${Math.floor(stepWidth / 2)}px;
  left: calc(${distanceBetweenSteps}% - ${Math.floor(stepWidth / 2)}px);
  border: ${stepBorder}px solid white;
`;

const WizardStep = styled.div`
  position: absolute;
  background-color: ${colors.SEAGULL};
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  ${({ distanceBetweenSteps }) => wizardStepMixin(STEP_SIZE, distanceBetweenSteps, STEP_SMALL_BORDER)};
  
  @media (max-width: 650px) {
    ${({ distanceBetweenSteps }) => wizardStepMixin(STEP_SMALL_SIZE, distanceBetweenSteps, STEP_SMALL_BORDER)}
  }
`;

const WizardStepInner = styled.div`
  background-color: ${({ inactive }) => (inactive ? colors.ALICE_BLUE : colors.SUPERNOVA)};
  color: ${({ inactive, isCompleted }) => (inactive && isCompleted ? colors.SUPERNOVA : colors.ALICE_BLUE)};
  border: 1px solid white;
  border-radius: 50%;
  height: ${STEP_INNER_SIZE}px;
  width: ${STEP_INNER_SIZE}px;
  opacity: ${({ inactive }) => (inactive ? 0.68 : 1)};
  font-size: 17px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  @media (max-width: 650px) {
    width: ${STEP_SMALL_INNER_SIZE}px;
    height: ${STEP_SMALL_INNER_SIZE}px;
  }
`;

const StepName = styled.span`
  position: absolute;
  top: 40px;
  width: 180px;
  opacity : ${({ inactive }) => (inactive ? 0.75 : 1)};
  font-weight: ${({ inactive }) => (inactive ? 'normal' : 'bold')};
  text-align: center;
  
  @media (max-width: 650px) {
    width: auto;
  }
`;
